import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { User, LogOut, Calendar, Settings, LayoutDashboard, Award, Shield } from 'lucide-react';
import { LogoMarkB, ScalimmoWordmark } from './Logo';

export const Navbar = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const isActive = (path) => location.pathname === path;

    return (
        <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/20 pt-safe" data-testid="navbar">
            <div className="container-scalimmo">
                <div className="flex items-center justify-between h-16 md:h-20">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
                        <LogoMarkB size={40} />
                        <ScalimmoWordmark size="md" />
                    </Link>

                    {/* Navigation Links */}
                    <div className="hidden md:flex items-center gap-8">
                        <Link
                            to="/agents"
                            className={`nav-link text-sm font-medium transition-colors ${
                                isActive('/agents') ? 'text-scalimmo-navy' : 'text-muted-foreground hover:text-scalimmo-navy'
                            }`}
                            data-testid="nav-agents"
                        >
                            Trouver un agent
                        </Link>
                        <Link
                            to="/top-performers"
                            className={`nav-link text-sm font-medium transition-colors flex items-center gap-1.5 ${
                                isActive('/top-performers') ? 'text-scalimmo-gold' : 'text-muted-foreground hover:text-scalimmo-gold'
                            }`}
                            data-testid="nav-top-performers"
                        >
                            <Award className="w-4 h-4" />
                            Top Performers
                        </Link>
                        <Link
                            to="/pricing"
                            className={`nav-link text-sm font-medium transition-colors ${
                                isActive('/pricing') ? 'text-scalimmo-navy' : 'text-muted-foreground hover:text-scalimmo-navy'
                            }`}
                            data-testid="nav-pricing"
                        >
                            Tarifs
                        </Link>
                    </div>

                    {/* Auth Section */}
                    <div className="flex items-center gap-4">
                        {user ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        className="flex items-center gap-2 rounded-full"
                                        data-testid="user-menu-trigger"
                                    >
                                        <div className="w-8 h-8 rounded-full bg-scalimmo-navy flex items-center justify-center">
                                            <span className="text-white text-sm font-medium">
                                                {user.first_name?.[0]}{user.last_name?.[0]}
                                            </span>
                                        </div>
                                        <span className="hidden md:inline text-sm font-medium">
                                            {user.first_name}
                                        </span>
                                        {user.is_top_performer && (
                                            <span className="elite-badge text-[10px] py-0.5 px-2">
                                                <Award className="w-3 h-3" />
                                                Elite
                                            </span>
                                        )}
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                    <div className="px-2 py-1.5">
                                        <p className="text-sm font-medium">{user.first_name} {user.last_name}</p>
                                        <p className="text-xs text-muted-foreground">{user.email}</p>
                                    </div>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                                        <LayoutDashboard className="mr-2 h-4 w-4" />
                                        Tableau de bord
                                    </DropdownMenuItem>
                                    {user.user_type === 'admin' && (
                                        <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                                            <Shield className="mr-2 h-4 w-4" />
                                            Administration
                                        </DropdownMenuItem>
                                    )}
                                    {user.user_type === 'agent' && (
                                        <DropdownMenuItem onClick={() => navigate('/dashboard/calendar')} data-testid="menu-calendar">
                                            <Calendar className="mr-2 h-4 w-4" />
                                            Mon calendrier
                                        </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem onClick={() => navigate('/dashboard/settings')} data-testid="menu-settings">
                                        <Settings className="mr-2 h-4 w-4" />
                                        Paramètres
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="menu-logout">
                                        <LogOut className="mr-2 h-4 w-4" />
                                        Déconnexion
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <>
                                <Button
                                    variant="ghost"
                                    className="hidden md:inline-flex rounded-full"
                                    onClick={() => navigate('/login')}
                                    data-testid="login-btn"
                                >
                                    Connexion
                                </Button>
                                <Button
                                    className="btn-primary"
                                    onClick={() => navigate('/register')}
                                    data-testid="register-btn"
                                >
                                    S'inscrire
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </nav>
    );
};
